import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ChevronLeft, ExternalLink, Printer, Save, CheckCircle2, Circle, Clock, Sparkles } from "lucide-react";

const STATUS_META = {
  not_started: { label: "Not Started", color: "bg-zinc-800 text-zinc-400 border-zinc-700", icon: Circle },
  in_progress: { label: "In Progress", color: "bg-amber-900/40 text-amber-300 border-amber-700", icon: Clock },
  submitted: { label: "Submitted", color: "bg-blue-900/40 text-blue-300 border-blue-700", icon: CheckCircle2 },
  approved: { label: "Approved", color: "bg-emerald-900/40 text-emerald-300 border-emerald-700", icon: CheckCircle2 },
};

export default function ClientSetupSprint() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [sprint, setSprint] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const pendingPatch = useRef({});
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sprintRes, schemaRes] = await Promise.all([
        axios.get(`${API}/client-sprints/${slug}`),
        axios.get(`${API}/client-sprints/schema`),
      ]);
      setSprint(sprintRes.data);
      setSchema(schemaRes.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to load sprint");
      if (e.response?.status === 404) navigate("/admin/client-sprints");
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const flushSave = useCallback(async () => {
    const patch = pendingPatch.current;
    if (!patch || Object.keys(patch).length === 0) return;
    pendingPatch.current = {};
    setSaving(true);
    try {
      const res = await axios.put(`${API}/client-sprints/${slug}`, patch);
      setSprint(res.data);
      setLastSavedAt(new Date());
    } catch (e) {
      console.error(e);
      toast.error("Save failed — retrying");
      // put the patch back so next flush will retry
      pendingPatch.current = { ...patch, ...pendingPatch.current };
    } finally {
      setSaving(false);
    }
  }, [slug]);

  const scheduleSave = useCallback(
    (patch) => {
      // deep-merge filings if present
      if (patch.filings) {
        pendingPatch.current.filings = pendingPatch.current.filings || {};
        for (const [fkey, fval] of Object.entries(patch.filings)) {
          const current = pendingPatch.current.filings[fkey] || {};
          const mergedFields = { ...(current.fields || {}), ...(fval.fields || {}) };
          pendingPatch.current.filings[fkey] = {
            ...current,
            ...fval,
            ...(Object.keys(mergedFields).length ? { fields: mergedFields } : {}),
          };
        }
        delete patch.filings;
      }
      pendingPatch.current = { ...pendingPatch.current, ...patch };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(flushSave, 700);
    },
    [flushSave]
  );

  // Save any pending edits on unmount / tab close
  useEffect(() => {
    const handler = () => flushSave();
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      flushSave();
    };
  }, [flushSave]);

  if (loading || !sprint || !schema) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  const submittedCount = Object.values(sprint.filings).filter(
    (f) => f.status === "submitted" || f.status === "approved"
  ).length;
  const totalFilings = schema.filings.length;
  const progressPct = Math.round((submittedCount / totalFilings) * 100);

  const updateMeta = (patch) => {
    setSprint((s) => ({ ...s, ...patch }));
    scheduleSave(patch);
  };

  const updateFiling = (fkey, patch) => {
    setSprint((s) => ({
      ...s,
      filings: {
        ...s.filings,
        [fkey]: {
          ...s.filings[fkey],
          ...patch,
          fields: { ...(s.filings[fkey]?.fields || {}), ...(patch.fields || {}) },
        },
      },
    }));
    scheduleSave({ filings: { [fkey]: patch } });
  };

  const setField = (fkey, fieldKey, value) => {
    updateFiling(fkey, { fields: { [fieldKey]: value }, status: sprint.filings[fkey].status === "not_started" ? "in_progress" : sprint.filings[fkey].status });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Sticky header */}
      <div className="sticky top-0 z-40 bg-black/90 backdrop-blur border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate("/admin/client-sprints")}
              className="text-zinc-400 hover:text-white shrink-0"
              data-testid="back-to-sprints-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <div className="text-xs text-emerald-400 uppercase tracking-wider font-semibold">Federal Contract Setup Sprint</div>
              <h1 className="text-xl sm:text-2xl font-bold truncate" data-testid="sprint-client-name">{sprint.client_name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="hidden sm:flex flex-col items-end text-xs">
              <span className="text-zinc-400">
                {saving ? (
                  <span className="text-amber-400 flex items-center gap-1"><Save className="w-3 h-3 animate-pulse" /> Saving…</span>
                ) : lastSavedAt ? (
                  <span className="text-emerald-400">Saved {lastSavedAt.toLocaleTimeString()}</span>
                ) : (
                  <span>Autosave on</span>
                )}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              className="border-zinc-700 hover:bg-zinc-900"
              data-testid="print-sprint-btn"
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3">
          <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
            <span>{submittedCount} of {totalFilings} filings submitted</span>
            <span>{progressPct}%</span>
          </div>
          <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${progressPct}%` }}
              data-testid="sprint-progress-bar"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Client meta */}
        <Card className="bg-zinc-950 border-zinc-800 p-5">
          <div className="text-sm font-semibold text-zinc-300 mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400" /> Client Info
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <MetaField label="Contact Name" value={sprint.contact_name} onChange={(v) => updateMeta({ contact_name: v })} testid="contact-name-input" />
            <MetaField label="Contact Phone" value={sprint.contact_phone} onChange={(v) => updateMeta({ contact_phone: v })} testid="contact-phone-input" />
            <MetaField label="Contact Email" value={sprint.contact_email} onChange={(v) => updateMeta({ contact_email: v })} testid="contact-email-input" />
            <MetaField label="Business Address" value={sprint.business_address} onChange={(v) => updateMeta({ business_address: v })} testid="business-address-input" />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Checkbox
              id="deposit-received"
              checked={!!sprint.deposit_received}
              onCheckedChange={(v) => updateMeta({ deposit_received: !!v, deposit_date: v ? new Date().toISOString() : null })}
              data-testid="deposit-received-checkbox"
            />
            <label htmlFor="deposit-received" className="text-sm text-zinc-300 cursor-pointer">
              Deposit received {sprint.deposit_date && <span className="text-zinc-500">— {new Date(sprint.deposit_date).toLocaleDateString()}</span>}
            </label>
          </div>
        </Card>

        {/* Timeline */}
        <Card className="bg-zinc-950 border-zinc-800 p-5">
          <div className="text-sm font-semibold text-zinc-300 mb-4">📅 15-Business-Day Delivery Timeline</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-emerald-400 border-b border-zinc-800">
                  <th className="py-2 pr-4 font-semibold w-24">Day</th>
                  <th className="py-2 font-semibold">Activity</th>
                </tr>
              </thead>
              <tbody>
                {schema.timeline.map((row) => (
                  <tr key={row.day} className="border-b border-zinc-900">
                    <td className="py-2 pr-4 text-zinc-400 whitespace-nowrap">{row.day}</td>
                    <td className="py-2 text-zinc-200">{row.activity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-xs text-zinc-500 italic">
            Note: SBA and state certifications continue processing beyond Day 15. Day 15 = our filings complete; agency review timelines run separately.
          </div>
        </Card>

        {/* Filings */}
        <div>
          <div className="text-sm font-semibold text-zinc-300 mb-3">7 Collaborative Filings</div>
          <Accordion type="multiple" defaultValue={[schema.filings[0].key]} className="space-y-2">
            {schema.filings.map((filingDef, idx) => {
              const filing = sprint.filings[filingDef.key] || { status: "not_started", fields: {}, notes: "" };
              const StatusIcon = STATUS_META[filing.status]?.icon || Circle;
              return (
                <AccordionItem
                  key={filingDef.key}
                  value={filingDef.key}
                  className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden"
                >
                  <AccordionTrigger
                    className="px-4 py-3 hover:no-underline hover:bg-zinc-900/50"
                    data-testid={`filing-toggle-${filingDef.key}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0 pr-2">
                      <div className="text-xs text-zinc-500 w-6 shrink-0">#{idx + 1}</div>
                      <StatusIcon className="w-4 h-4 shrink-0 text-zinc-400" />
                      <div className="text-left flex-1 min-w-0">
                        <div className="font-semibold text-white truncate">{filingDef.label}</div>
                      </div>
                      <Badge className={`${STATUS_META[filing.status]?.color} text-xs shrink-0`}>
                        {STATUS_META[filing.status]?.label}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-5 pt-2">
                    <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-zinc-900">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">Status:</span>
                        <Select
                          value={filing.status}
                          onValueChange={(v) => updateFiling(filingDef.key, { status: v })}
                        >
                          <SelectTrigger className="w-40 h-8 bg-zinc-900 border-zinc-700 text-xs" data-testid={`filing-status-${filingDef.key}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                            {Object.entries(STATUS_META).map(([k, m]) => (
                              <SelectItem key={k} value={k}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {filingDef.url && (
                        <a
                          href={filingDef.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                          data-testid={`filing-open-url-${filingDef.key}`}
                        >
                          Open filing site <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {filing.submitted_at && (
                        <span className="text-xs text-blue-400">
                          Submitted {new Date(filing.submitted_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      {filingDef.fields.map((f) => (
                        <FilingField
                          key={f.key}
                          def={f}
                          value={filing.fields?.[f.key]}
                          onChange={(v) => setField(filingDef.key, f.key, v)}
                          testid={`field-${filingDef.key}-${f.key}`}
                        />
                      ))}
                    </div>

                    <div className="mt-4">
                      <label className="text-xs text-zinc-400 mb-1 block">Notes / next steps</label>
                      <Textarea
                        rows={2}
                        value={filing.notes || ""}
                        onChange={(e) => updateFiling(filingDef.key, { notes: e.target.value })}
                        className="bg-zinc-900 border-zinc-700 text-white text-sm"
                        placeholder="e.g. Gorden needs to bring EIN letter next visit"
                        data-testid={`filing-notes-${filingDef.key}`}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="text-center text-xs text-zinc-600 py-6">
          Kennardo James | Federal Contract Sprints · Autosaves every keystroke
        </div>
      </div>
    </div>
  );
}

function MetaField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1 block">{label}</label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-zinc-900 border-zinc-700 text-white text-sm"
        data-testid={testid}
      />
    </div>
  );
}

function FilingField({ def, value, onChange, testid }) {
  if (def.type === "textarea") {
    return (
      <div className="sm:col-span-2">
        <label className="text-xs text-zinc-400 mb-1 block">{def.label}</label>
        <Textarea
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="bg-zinc-900 border-zinc-700 text-white text-sm"
          data-testid={testid}
        />
      </div>
    );
  }
  if (def.type === "checkbox") {
    return (
      <div className="sm:col-span-2 flex items-center gap-2">
        <Checkbox
          id={testid}
          checked={!!value}
          onCheckedChange={(v) => onChange(!!v)}
          data-testid={testid}
        />
        <label htmlFor={testid} className="text-sm text-zinc-300 cursor-pointer">{def.label}</label>
      </div>
    );
  }
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1 block">{def.label}</label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-zinc-900 border-zinc-700 text-white text-sm"
        data-testid={testid}
      />
    </div>
  );
}
